<div
  class="max-w-6xl mx-auto p-5 md:p-8 max-h-[calc(100vh-10rem)] overflow-auto"
>
  <!-- Header -->
  <div class="mb-8 text-center">
    <h2 class="text-3xl font-bold text-gray-800 mb-2">Mis Devoluciones</h2>
    <p class="text-gray-600">
      Gestiona y consulta el estado de tus solicitudes de devolución
    </p>
    <p class="text-gray-600">
      Si no solicita el reembolso en 2 días, se realizará de manera automática
      el reembolso en subicoins.
    </p>
  </div>

  <!-- Loading Skeleton -->
  @if (isLoading) {
  <div class="space-y-4">
    @for (item of [1, 2, 3, 4, 5]; track item) {

    <div class="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
      <div class="flex justify-between items-center mb-4">
        <div class="skeleton-line rounded w-48 h-5"></div>
        <div class="skeleton-line rounded-full w-24 h-6"></div>
      </div>
      <div class="space-y-3">
        <div class="skeleton-line rounded w-full h-4"></div>
        <div class="skeleton-line rounded w-3/5 h-4"></div>
        <div class="skeleton-line rounded w-32 h-5"></div>
      </div>
    </div>
    }
  </div>
  }

  <!-- Error State -->
  @if (hasError && !isLoading) {
  <div
    class="text-center py-12 px-6 bg-white rounded-xl shadow-sm border border-gray-200"
  >
    <div class="mb-4">
      <svg
        class="mx-auto"
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
          fill="#ef4444"
        />
      </svg>
    </div>
    <h3 class="text-xl font-semibold text-red-500 mb-2">
      Error al cargar las devoluciones
    </h3>
    <p class="text-gray-600 mb-6">{{ errorMessage }}</p>
    <button
      class="bg-blue-500 hover:bg-blue-600 text-white font-medium px-6 py-3 rounded-lg transition-colors"
      (click)="loadDevoluciones()"
    >
      Reintentar
    </button>
  </div>
  }

  <!-- Empty State -->
  @if (!isLoading && !hasError && devoluciones.length === 0) {
  <div
    class="text-center py-12 px-6 bg-white rounded-xl shadow-sm border border-gray-200"
  >
    <div class="mb-4">
      <svg
        class="mx-auto"
        width="64"
        height="64"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M19 7h-3V6a4 4 0 0 0-8 0v1H5a1 1 0 0 0-1 1v11a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V8a1 1 0 0 0-1-1zM10 6a2 2 0 0 1 4 0v1h-4V6zm8 13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V9h2v1a1 1 0 0 0 2 0V9h4v1a1 1 0 0 0 2 0V9h2v10z"
          fill="#9ca3af"
        />
      </svg>
    </div>
    <h3 class="text-xl font-semibold text-gray-700 mb-2">
      No tienes devoluciones
    </h3>
    <p class="text-gray-600">Cuando solicites una devolución, aparecerá aquí</p>
  </div>
  }

  <!-- Devoluciones List -->
  @if (!isLoading && !hasError && devoluciones.length > 0) {
  <div class="space-y-4">
    @for (devolucion of devoluciones; track trackByOrderId($index, devolucion))
    {
    <div
      class="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
    >
      <div
        class="flex flex-col md:flex-row md:justify-between md:items-start mb-4 space-y-3 md:space-y-0"
      >
        <div class="flex items-center">
          <div class="flex">
            <div class="flex-1/2">
              <img
                class="w-12 h-12 rounded-full"
                [src]="getImageUrl(devolucion.picture_logo)"
                [alt]="devolucion.name_restaurant"
                (error)="onImageError($event)"
              />
            </div>
            <div class="flex-1/2">
              <span class="text-sm font-medium text-gray-600 sm:font-normal">{{
                devolucion.name_restaurant
              }}</span>
            </div>
          </div>
        </div>
        <div class="space-y-1">
          <h3 class="text-lg font-semibold text-gray-800">
            Pedido #{{ devolucion.no_order }}
          </h3>
          <span class="text-sm text-gray-500">{{
            formatDate(devolucion.created_at)
          }}</span>
        </div>
        <div>
          <span
            class="inline-flex px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wide"
            [ngClass]="{
              'bg-yellow-100 text-yellow-800':
                getStatusClass(devolucion.statusShoppingCart) ===
                'status-pending',
              'bg-green-100 text-green-800':
                getStatusClass(devolucion.statusShoppingCart) ===
                'status-approved',
              'bg-red-100 text-red-800':
                getStatusClass(devolucion.statusShoppingCart) ===
                'status-rejected',
              'bg-blue-100 text-blue-800':
                getStatusClass(devolucion.statusShoppingCart) ===
                'status-completed',
              'bg-gray-100 text-gray-800':
                getStatusClass(devolucion.statusShoppingCart) ===
                'status-default'
            }"
          >
            {{ getStatusText(devolucion.statusShoppingCart) }}
          </span>
        </div>
      </div>
      <div class="space-y-3 mb-5">
        <div
          class="flex flex-col md:flex-row md:justify-between md:items-center space-y-1 md:space-y-0"
        >
          <span class="text-sm font-medium text-gray-600"
            >Método de devolución:</span
          >
          <span class="text-sm font-medium text-gray-800">{{
            devolucion.refundMethod
          }}</span>
        </div>
        <div
          class="flex flex-col md:flex-row md:justify-between md:items-center space-y-1 md:space-y-0"
        >
          <span class="text-sm font-medium text-gray-600"
            >Estado de aprobación:</span
          >
          <span
            class="text-sm font-medium"
            [ngClass]="{
              'text-green-600': devolucion.isAcceppt,
              'text-amber-600': !devolucion.isAcceppt
            }"
          >
            {{ devolucion.isAcceppt ? "Aprobada" : "Pendiente" }}
          </span>
        </div>
        <div
          class="flex flex-col md:flex-row md:justify-between md:items-center p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-1 md:space-y-0"
        >
          <span class="text-sm font-medium text-gray-600"
            >Monto a devolver:</span
          >
          <span class="text-lg font-bold text-gray-800">{{
            formatCurrency(devolucion.total)
          }}</span>
        </div>
        <!-- Desglose de montos por método -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
          @if (devolucion.cardAmount > 0) {
          <div class="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
            <i class="fas fa-credit-card text-gray-600 mr-3"></i>
            <div>
              <div class="text-xs font-medium text-gray-600">Tarjeta</div>
              <div class="text-sm font-semibold text-gray-800">{{
                formatCurrency(devolucion.cardAmount)
              }}</div>
            </div>
          </div>
          }

          @if (devolucion.creditAmount > 0) {
          <div class="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
            <i class="fas fa-coins text-amber-600 mr-3"></i>
            <div>
              <div class="text-xs font-medium text-gray-600">Créditos</div>
              <div class="text-sm font-semibold text-gray-800">{{
                formatCurrency(devolucion.creditAmount)
              }}</div>
            </div>
          </div>
          }

          @if (devolucion.cashAmount > 0) {
          <div class="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
            <i class="fas fa-money-bill-wave text-green-600 mr-3"></i>
            <div>
              <div class="text-xs font-medium text-gray-600">Efectivo</div>
              <div class="text-sm font-semibold text-gray-800">{{
                formatCurrency(devolucion.cashAmount)
              }}</div>
            </div>
          </div>
          }
        </div>
      </div>
      @if(!devolucion.isAcceppt){
      <div class="flex justify-end">
        <button
          (click)="requestRefund(devolucion)"
          class="bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 hover:border-gray-400 px-4 py-2 rounded-md text-sm font-medium transition-all"
        >
          solicitar devolución
        </button>
      </div>
      }
    </div>
    }
  </div>
  }

  <!-- Modal Component - Always rendered -->
  <solicitar-devolucion
    #solicitarDevolucion
    [idOrden]="0"
    (reload)="loadDevoluciones()"
  ></solicitar-devolucion>
</div>
